/**
 * Hisbah (Partner Portal) – PO Invoice page.
 * Calls GET partner-portal/po/invoice?supplier_order_id={id}
 * Renders invoice_number, invoice_date when available, and invoice_images.
 */
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import SharedData from '../../modules/common/SharedData'
import { getPOInvoice } from '../../modules/orders/core/_requests'
import Loader from '../../modules/common/Loader'
import {API_URL} from '../../tenant/apiBase'

export interface POInvoiceImage {
  id: number
  image: string
  display_label: string | null
}

export interface POInvoiceData {
  invoice_number: string | null
  invoice_date: string | null
  invoice_images: POInvoiceImage[]
}

interface POInvoicePageProps {
  supplierOrderId: number | string | null
}

function ImagePreviewModal({
  imageUrl,
  altText,
  show,
  onClose,
}: {
  imageUrl: string | null
  altText: string
  show: boolean
  onClose: () => void
}) {
  if (!show || !imageUrl) return null
  return (
    <div
      className='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-4'
      style={{ backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 1050 }}
      onClick={onClose}
    >
      <button
        type='button'
        className='position-absolute top-0 end-0 m-3 btn btn-link text-white text-decoration-none fs-2'
        onClick={onClose}
        aria-label='Close'
      >
        ×
      </button>
      <img
        src={imageUrl}
        alt={altText}
        className='img-fluid'
        style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />
    </div>
  )
}

export function POInvoicePage({ supplierOrderId }: POInvoicePageProps) {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const { currentUser, language } = SharedData()
  const [data, setData] = useState<POInvoiceData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lightboxImage, setLightboxImage] = useState<{ url: string; alt: string } | null>(null)

  useEffect(() => {
    if (!supplierOrderId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    getPOInvoice(String(supplierOrderId), currentUser?.token, language)
      .then((res) => {
        const raw = res.data as { data?: POInvoiceData } | POInvoiceData
        const payload = 'data' in raw && raw.data ? raw.data : (raw as POInvoiceData)
        if (!payload) throw new Error('Invalid response')
        setData({
          invoice_number: payload.invoice_number ?? null,
          invoice_date: payload.invoice_date ?? null,
          invoice_images: Array.isArray(payload.invoice_images) ? payload.invoice_images : [],
        })
      })
      .catch((err) => {
        const msg =
          err.response?.data?.message ||
          (err.response?.status ? `Error ${err.response.status}` : null) ||
          err.message
        setError(msg)
      })
      .finally(() => setLoading(false))
  }, [supplierOrderId, currentUser?.token, language])

  if (loading) return <Loader />
  if (error) {
    return (
      <div className='card'>
        <div className='card-body'>
          <div className='text-danger'>{error}</div>
          <button type='button' className='btn btn-light-primary mt-3' onClick={() => navigate(-1)}>
            {formatMessage({ id: 'back', defaultMessage: '← Back' })}
          </button>
        </div>
      </div>
    )
  }
  if (!data) return null

  const { invoice_number, invoice_date, invoice_images } = data
  const imageBase = API_URL || ''

  const toAbsoluteImageUrl = (url: string) =>
    url && (url.startsWith('http://') || url.startsWith('https://')) ? url : `${imageBase}${url}`

  return (
    <>
      <div className='card'>
        <div className='card-header border-0 pt-5 d-flex flex-wrap align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-wrap align-items-center gap-3'>
            <button
              type='button'
              className='btn btn-light-primary btn-sm'
              onClick={() => navigate(-1)}
              aria-label={formatMessage({ id: 'back', defaultMessage: '← Back' })}
            >
              {formatMessage({ id: 'back', defaultMessage: '← Back' })}
            </button>
            <h3 className='card-title mb-0'>
              {formatMessage({ id: 'invoice_for_po_id', defaultMessage: 'Invoice for PO ID:' })}{' '}
              <span className='fw-bold'>{supplierOrderId}</span>
            </h3>
          </div>
        </div>
        <div className='card-body'>
          {(invoice_number != null && invoice_number !== '') || (invoice_date != null && invoice_date !== '') ? (
            <div className='po-invoice-header d-flex flex-wrap align-items-center gap-3 mb-5 pb-4 border-bottom'>
              {invoice_number != null && invoice_number !== '' && (
                <span className='text-gray-700'>
                  <strong>Invoice number:</strong> {invoice_number}
                </span>
              )}
              {invoice_date != null && invoice_date !== '' && (
                <span className='text-gray-700'>
                  <strong>Invoice date:</strong> {invoice_date}
                </span>
              )}
            </div>
          ) : null}
          <div className='po-invoice-images'>
            {invoice_images.length === 0 ? (
              <p className='text-muted mb-0'>No invoice images.</p>
            ) : (
              <div className='row g-4'>
                {invoice_images.map((img) => (
                  <div key={img.id} className='col-12 col-md-6 col-lg-4'>
                    <div className='card border h-100'>
                      <div className='card-body'>
                        {img.display_label != null && img.display_label !== '' && (
                          <div className='po-invoice-image-label fw-semibold mb-2'>{img.display_label}</div>
                        )}
                        {img.image && (
                          <div
                            role='button'
                            tabIndex={0}
                            className='rounded overflow-hidden bg-light d-inline-block'
                            style={{ cursor: 'pointer', maxWidth: '100%' }}
                            onClick={() =>
                              setLightboxImage({
                                url: toAbsoluteImageUrl(img.image),
                                alt: img.display_label || `Invoice ${img.id}`,
                              })
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setLightboxImage({
                                  url: toAbsoluteImageUrl(img.image),
                                  alt: img.display_label || `Invoice ${img.id}`,
                                })
                              }
                            }}
                          >
                            <img
                              src={toAbsoluteImageUrl(img.image)}
                              alt={img.display_label || `Invoice ${img.id}`}
                              className='po-invoice-image img-fluid'
                              style={{ display: 'block' }}
                            />
                          </div>
                        )}
                        <small className='text-muted mt-2 d-block'>
                          {formatMessage({
                            id: 'click_image_to_enlarge',
                            defaultMessage: 'Click image to enlarge',
                          })}
                        </small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ImagePreviewModal
        show={!!lightboxImage}
        imageUrl={lightboxImage?.url ?? null}
        altText={lightboxImage?.alt ?? 'Invoice'}
        onClose={() => setLightboxImage(null)}
      />
    </>
  )
}

/** Wrapper that reads orderID from route params and provides it to the invoice page. */
export function POInvoicePageFromRoute() {
  const { orderID } = useParams<{ orderID: string }>()
  return (
    <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
      <div className='col-12'>
        <POInvoicePage supplierOrderId={orderID ?? null} />
      </div>
    </div>
  )
}

export default POInvoicePage
